import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { Alert, Snackbar } from "@mui/material";
import { browserLocales, isMacOS, isSafari } from "../player/Utils";
import { clearQueryString } from "../maker/MakerController";
import { analyticsManager, app, testedGroup } from "../index";
import { getAuth } from "firebase/auth";
import { getId, getInstallations } from "@firebase/installations";
import { AppContext } from "../AppContext";
import MaterialButton from "../maker/component/MaterialButton";
import LoginDialog from "../maker/LoginDialog";
import Create1ClicDialog from "../maker/component/Create1ClicDialog";
import Faq from "react-faq-component";
import LineBreak from "../maker/component/LineBreak";
import ImageModal from "../maker/component/ImageModal";
import Player from "../player/Player";
import WebPageMock from "../player/side_menu/components/WebPageMock";
import TestimonialsSection from "../HP/TestimonialSection";
import { useScrollAnimations } from "../hooks/useScrollAnimations";
import '../ProductPage.css';

function BaseHomePage({ 
  config,
  onLogin,
  setHomePageOrigin,
  pageTitle,
  origin
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const [showLogin, setShowLogin] = useState(false);
  const [showAckDisconnected, setShowAckDisconnected] = useState(new URLSearchParams(window.location.search).get("ackDisconnected") === "true");
  const [showAckAccountDeleted, setShowAckAccountDeleted] = useState(new URLSearchParams(window.location.search).get("ackAccountDeleted") === "true");
  const [showErrorEmailLinkExpired, setShowErrorEmailLinkExpired] = useState(new URLSearchParams(window.location.search).get("errLinkExpired") === "true");    
  const [showErrorRegistrationLimitReached, setShowErrorRegistrationLimitReached] = useState(new URLSearchParams(window.location.search).get("errRegistrationLimit") === "true");    
  const [showError, setShowError] = useState(new URLSearchParams(window.location.search).get("error") === "true");    
  const [loginTitle, setLoginTitle] = useState(new URLSearchParams(window.location.search).get("login_title"));
  const [showCreate1Clic, setShowCreate1Clic] = useState(false);
  // Languette "taux de réservation" : masquée après interaction player, réaffichée au scroll page
  const [isDesktopDemoHovered, setIsDesktopDemoHovered] = useState(false);
  // Force HP re-render on resize so deskTop / compactLaptop metrics & Players update
  const [viewportSize, setViewportSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const language = browserLocales(true)[0];

  document.title = t(pageTitle);

  useEffect(() => {
    let rafId = null;
    function handleResize() {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        setViewportSize((prev) => {
          const width = window.innerWidth;
          const height = window.innerHeight;
          if (prev.width === width && prev.height === height) {
            return prev;
          }
          return { width, height };
        });
        rafId = null;
      });
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    // Safari macOS : languette masquée jusqu'au prochain scroll (évite le bug à l'ouverture des options player)
    if (!(isMacOS() && isSafari())) {
      return undefined;
    }
    function handlePageScroll() {
      setIsDesktopDemoHovered(false);
    }
    window.addEventListener("scroll", handlePageScroll, { passive: true });
    return () => window.removeEventListener("scroll", handlePageScroll);
  }, []);

  useEffect(() => {   
    setHomePageOrigin(config.homePageOrigin);
    getAuth().authStateReady()
      .then(() =>{  
        const currentUser = getAuth().currentUser;
        if (currentUser == null){
          setUserIdFromFirebaseId();
        }    
        else if (currentUser.isAnonymous){
          analyticsManager.logHomePageScreenViewTag();
        } else {
          // Déjà connecté (compte réel) → espace logged
          navigate("/logged");
        }
      });

    async function setUserIdFromFirebaseId() {
      const firebaseInstallationId = await getId(getInstallations(app));            
      analyticsManager.setUserId(firebaseInstallationId);
      analyticsManager.setUserProperty(testedGroup, null);
      analyticsManager.logHomePageScreenViewTag();
    }
  }, []);

  const scriptList = document.querySelectorAll("script[type='text/javascript']");
  const convertedNodeList = Array.from(scriptList);
  const hubspotScript = convertedNodeList.find(script => script.id === "hs-script-loader");

  useEffect(() => {
    if (!isTabletOrMobile && hubspotScript == null){
      const script = document.createElement('script');
  
      script.id = "hs-script-loader";
      script.src = "//js-eu1.hs-scripts.com/143936779.js";
      script.async = true;
      script.defer = true;
      script.type = "text/javascript";
  
      document.body.appendChild(script);
    }

    const faqTexts = document.getElementsByClassName("row-content-text");
    if (faqTexts != null){
      for (let i = 0; i < faqTexts.length; i++) {
        faqTexts[i].style.whiteSpace = "pre-line";
      }
    }
    const faqListRoles = document.querySelectorAll(".faq-body[role='list'], .styles_row-body__1NvUo[role='list']");
    faqListRoles.forEach((el) => el.removeAttribute("role"));
    const faqListItemRoles = document.querySelectorAll(".faq-row[role='listitem'], .styles_faq-row__2YF3c[role='listitem']");
    faqListItemRoles.forEach((el) => el.removeAttribute("role"));
  }, []);

  // Rebind quand desktop/mobile change ; resize géré dans le hook
  useScrollAnimations(isTabletOrMobile ? "mobile" : "desktop");

  useEffect(() => {
    function logFaq(position, faqTheme){
      return () => {
        if (document.getElementsByClassName("row-title")[position].className.includes("closed"))
          analyticsManager.logReadFAQTag(origin, faqTheme);
      };
    }        

    const handleClickListeners = config.faqConfig.map((faq, index) => {
      const listener = logFaq(index, faq.theme);
      document.getElementsByClassName("row-title")[index].addEventListener("click", listener);
      return listener;
    });

    return () => {
      handleClickListeners.forEach((listener, index) => {
        window.removeEventListener('click', listener);
      });
    };
  }, []);

  document.getElementsByTagName('body')[0].className = 'WhiteBackground';
  document.getElementById("theme-color").setAttribute("content", "#FFFFFF");

  const styles = {
    bgColor: "transparent",
    titleTextColor: "rgb(45, 55, 72)",
    titleTextSize: "26px",
    rowTitleColor: "rgb(45, 55, 72)",
    rowContentColor: "rgb(45, 55, 72)",
    arrowColor: "#2FC4B5",
    rowContentPaddingTop: "10px",
    rowContentPaddingBottom: "10px", 
  };

  function showAckDisconnectedSnack(){
    if (showAckDisconnected){            
      clearQueryString();
      return (
        <Snackbar open={showAckDisconnected} onClose={()=>setShowAckDisconnected(false)} anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert onClose={()=>setShowAckDisconnected(false)} severity="success" sx={{ width: '100%' }}>     
          {t('AckDisconnected')}
          </Alert>
        </Snackbar>
      );
    }
    else return null;
  }

  function showAckAccountDeletedSnack(){
    if (showAckAccountDeleted){            
      return (
        <Snackbar open={showAckAccountDeleted} onClose={()=>setShowAckAccountDeleted(false)} anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert onClose={()=>setShowAckAccountDeleted(false)} severity="success" sx={{ width: '100%' }}>     
          {t('AckAccountDeleted')}
          </Alert>
        </Snackbar>
      );
    }
    else return null;
  }

  function showErrorEmailLinkExpiredSnack(){
    if (showErrorEmailLinkExpired){
      clearQueryString();
      return (
        <Snackbar open={showErrorEmailLinkExpired} autoHideDuration={6000} onClose={()=>setShowErrorEmailLinkExpired(false)} anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert onClose={()=>setShowErrorEmailLinkExpired(false)} severity="error" sx={{ width: '100%' }}>     
          {t('ErrorEmailLinkExpired')}
          </Alert>
        </Snackbar>
      );
    }
    else return null;
  }

  function showErrorRegistrationLimitReachedSnack(){
    if (showErrorRegistrationLimitReached){
      clearQueryString();
      return (
        <Snackbar open={showErrorRegistrationLimitReached} autoHideDuration={8000} onClose={()=>setShowErrorRegistrationLimitReached(false)} anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert onClose={()=>setShowErrorRegistrationLimitReached(false)} severity="error" sx={{ width: '100%' }}>     
          {t('ErrorSubscriptionLimitReached')}
          </Alert>
        </Snackbar>
      );
    }
    else return null;
  }

  function showErrorSnack(){
    if (showError){
      clearQueryString();
      return (
        <Snackbar open={showError} autoHideDuration={8000} onClose={()=>setShowError(false)} anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
          <Alert onClose={()=>setShowError(false)} severity="error" sx={{ width: '100%' }}>     
          {t('ErrorDefault')}
          </Alert>
        </Snackbar>
      );
    }
    else return null;
  }

  const hasScrolledDemo = useRef(false);
  const lastScrollTime = useRef(0);
  useEffect(() => {
    // Vérifier si cette page a besoin de la fonctionnalité web_demo
    if (!config.hasWebDemo) {
      return;
    }
    
    const frame = document.getElementById('web_demo');     
    
    // Vérifier si l'élément web_demo existe
    if (!frame) {
      return;
    }
    
    function scrollHandler() {
      if (Date.now() - lastScrollTime.current > 2000){
        hasScrolledDemo.current = false;
      }
      if (!hasScrolledDemo.current){
        analyticsManager.logScrollInWebDemoTag();
        hasScrolledDemo.current = true;
        lastScrollTime.current = Date.now();
      }
    }

    function clickHandler() {
      analyticsManager.logClickInWebDemoTag();
    }

    function loadHandler() {
      if (frame.contentDocument) {
        frame.contentDocument.addEventListener('click', clickHandler);
        frame.contentDocument.addEventListener('scroll', scrollHandler);
      }
    }
    frame.addEventListener('load', loadHandler);        

    return () => {
      frame.removeEventListener('load', loadHandler);   
      if (frame.contentDocument) {
        frame.removeEventListener('click', clickHandler);     
        frame.removeEventListener('scroll', scrollHandler);
      }
    }
  }, []);

  // Rendu conditionnel selon la configuration
  // viewportSize: resize re-renders HP so deskTop / compactLaptop / Players update
  const mainContent = !isTabletOrMobile 
    ? config.desktopRenderer({
        t,
        navigate,
        language,
        analyticsManager,
        MaterialButton,
        ImageModal,
        Player,
        WebPageMock,
        TestimonialsSection,
        Faq,
        styles,
        showLogin,
        setShowLogin,
        loginTitle,
        setLoginTitle,
        showCreate1Clic,
        setShowCreate1Clic,
        isDesktopDemoHovered,
        setIsDesktopDemoHovered,
        onLogin,
        viewportHeight: viewportSize.height,
        viewportWidth: viewportSize.width,
      })
    : config.mobileRenderer({
        t,
        navigate,
        language,
        analyticsManager,
        MaterialButton,
        LineBreak,
        Player,
        WebPageMock,
        TestimonialsSection,
        Faq,
        styles,
        showLogin,
        setShowLogin,
        loginTitle,
        setLoginTitle,
        showCreate1Clic,
        setShowCreate1Clic,
        onLogin,
        viewportHeight: viewportSize.height,
        viewportWidth: viewportSize.width,
      });

  // Retourner le contenu principal avec les composants de dialogue
  return (
    <>
      {mainContent}
      <LoginDialog 
        origin={origin} 
        showLogin={showLogin !== false} 
        customLoginButton={showLogin != false} 
        title={loginTitle} 
        resetLoginDialog={setShowLogin} 
        onSuccess={(result) => onLogin(result.user)}
      />
      {showAckDisconnectedSnack()}
      {showAckAccountDeletedSnack()}
      {showErrorEmailLinkExpiredSnack()}
      {showErrorRegistrationLimitReachedSnack()}
      {showErrorSnack()}
      <Create1ClicDialog 
        show={showCreate1Clic} 
        closeDialog={setShowCreate1Clic} 
        origin={origin}
      />
    </>
  );
}

export default BaseHomePage; 